import { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

export default function RateDialog({ userId, name, onRated }) {
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get(`/ratings/${userId}`).then((r) => setStars(r.data.my_rating || 0)).catch(() => {});
    }
  }, [open, userId]);

  const submit = async () => {
    if (!stars) { toast.error("Pick a star rating"); return; }
    setSaving(true);
    try {
      await api.post("/ratings", { ratee_id: userId, stars, comment });
      toast.success(`You rated ${name} ${stars}★`);
      setOpen(false);
      onRated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rating");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-rate-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-amber-400/60 hover:text-amber-400 transition-all">
          <Star className="w-3.5 h-3.5" /> Rate
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Rate {name}</DialogTitle>
          <DialogDescription>How reliable were they to work with?</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-2 py-3">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} data-testid={`star-${n}`} type="button"
              onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setStars(n)}>
              <Star style={{ width: 34, height: 34 }}
                className={`transition-colors ${n <= (hover || stars) ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30"}`} />
            </button>
          ))}
        </div>
        <Textarea data-testid="rate-comment" value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Optional note (on-time, communication, cargo handling…)" />
        <DialogFooter>
          <Button data-testid="submit-rating-btn" onClick={submit} disabled={saving} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? "Saving…" : "Submit rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
